import React, { useCallback, useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Printer, CheckCircle, Clock } from "lucide-react";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./BillingManagementView.module.scss";

const BillingManagementView = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const invoiceRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.includes("/admin");

  const handleDownloadPDF = () => {
    const element = invoiceRef.current;
    const opt = {
      margin: [0.5, 0.5],
      filename: `Invoice-${data.invoice_no}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  const handlePrint = () => {
    window.print();
  };

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await apiGet("billing", `/pay-download/${id}`);
      if (res?.status === "success" && res?.data?.length) {
        setData(res.data[0]);
      }
    } catch (error) {
      console.error("Invoice fetch error", error);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  if (!data) return null;

  const isPaid = data.status === "Paid";
  const isRajasthan = data?.company_detail?.state === "Rajasthan";

  return (
    <div className={styles["admin-billing-manage-view__page"]}>
      {/* HEADER SECTION */}
      <div className={styles["admin-billing-manage-view__header"]}>
        <div className={styles["admin-billing-manage-view__title-area"]}>
          <button
            className={styles["admin-billing-manage-view__back-btn"]}
            onClick={() =>
              navigate(isAdmin ? "/admin/billing-management" : "/billing")
            }
            aria-label="Go Back"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>

          <div className={styles["admin-billing-manage-view__title-group"]}>
            <h2 className={styles["admin-billing-manage-view__title"]}>
              Invoice
            </h2>
            <p className={styles["admin-billing-manage-view__subtitle"]}>
              Invoice #{data.invoice_no}
            </p>
          </div>
        </div>

        <div className={styles["admin-billing-manage-view__actions"]}>
          <button
            className={`${styles["admin-billing-manage-view__btn"]} ${styles["admin-billing-manage-view__btn--secondary"]}`}
            onClick={handlePrint}
          >
            <Printer size={16} strokeWidth={2.5} />
            <span>Print</span>
          </button>

          <button
            className={`${styles["admin-billing-manage-view__btn"]} ${styles["admin-billing-manage-view__btn--primary"]}`}
            onClick={handleDownloadPDF}
          >
            <Download size={16} strokeWidth={2.5} />
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* PRINTABLE INVOICE CARD */}
      <div className={styles["admin-billing-manage-view__invoice-card"]}>
        <div
          ref={invoiceRef}
          id="invoice-print-area"
          className={styles["admin-billing-manage-view__print-area"]}
        >
          {/* Status Banner */}
          <div
            className={`${styles["admin-billing-manage-view__status-banner"]} ${
              isPaid
                ? styles["admin-billing-manage-view__status-banner--paid"]
                : styles["admin-billing-manage-view__status-banner--pending"]
            }`}
          >
            {isPaid ? <CheckCircle size={36} /> : <Clock size={36} />}
            <div className={styles["admin-billing-manage-view__status-text"]}>
              <h3 className={styles["admin-billing-manage-view__status-title"]}>
                {isPaid ? "Payment Successful" : "Payment Pending"}
              </h3>
              <p className={styles["admin-billing-manage-view__status-desc"]}>
                {isPaid
                  ? "Your credits have been securely added to your account."
                  : "Your payment is currently pending processing."}
              </p>
            </div>
          </div>

          <div className={styles["admin-billing-manage-view__body"]}>
            {/* Addresses */}
            <div className={styles["admin-billing-manage-view__address-grid"]}>
              {/* FROM */}
              <div
                className={styles["admin-billing-manage-view__address-block"]}
              >
                <span className={styles["admin-billing-manage-view__label"]}>
                  From
                </span>
                {/* <h4
                  className={styles["admin-billing-manage-view__company-name"]}
                >
                  {data.billing_detail?.name || "Doql"}
                </h4> */}
                <p className={styles["admin-billing-manage-view__text"]}>
                  {data.billing_detail?.companyName} <br />
                  {data.billing_detail?.address} <br />
                  {data.billing_detail?.state} <br />
                  {data.billing_detail?.email} <br />
                  <strong>GSTIN:</strong> {data.billing_detail?.gstNo}
                </p>
              </div>

              {/* BILL TO */}
              <div
                className={styles["admin-billing-manage-view__address-block"]}
              >
                <span className={styles["admin-billing-manage-view__label"]}>
                  Bill To
                </span>
                <h4
                  className={styles["admin-billing-manage-view__company-name"]}
                >
                  {data.company_detail?.name}
                </h4>
                <p className={styles["admin-billing-manage-view__text"]}>
                  {data.company_detail?.companyName} <br />
                  {data.company_detail?.address} <br />
                  {data.company_detail?.state} <br />
                  {data.company_detail?.email} <br />
                  <strong>GSTIN:</strong> {data.company_detail?.gstNo}
                </p>
              </div>
            </div>

            {/* Meta Info Bar */}
            <div className={styles["admin-billing-manage-view__meta-grid"]}>
              <div className={styles["admin-billing-manage-view__meta-item"]}>
                <span className={styles["admin-billing-manage-view__label"]}>
                  Invoice Date
                </span>
                <span className={styles["admin-billing-manage-view__text"]}>
                  {data.invoice_date?.split("T")[0]}
                </span>
              </div>
              <div className={styles["admin-billing-manage-view__meta-item"]}>
                <span className={styles["admin-billing-manage-view__label"]}>
                  Payment Method
                </span>
                <span className={styles["admin-billing-manage-view__text"]}>
                  {data.payment_method}
                </span>
              </div>
              <div className={styles["admin-billing-manage-view__meta-item"]}>
                <span className={styles["admin-billing-manage-view__label"]}>
                  Transaction ID
                </span>
                <span className={styles["admin-billing-manage-view__text"]}>
                  {data.transaction_id || "N/A"}
                </span>
              </div>
            </div>

            {/* Item Table */}
            <div className={styles["admin-billing-manage-view__table-wrapper"]}>
              <table className={styles["admin-billing-manage-view__table"]}>
                <thead>
                  <tr>
                    <th className={styles["admin-billing-manage-view__th"]}>
                      Description
                    </th>
                    <th
                      className={styles["admin-billing-manage-view__th"]}
                      style={{ textAlign: "center" }}
                    >
                      Quantity
                    </th>
                    <th
                      className={styles["admin-billing-manage-view__th"]}
                      style={{ textAlign: "right" }}
                    >
                      Unit Price
                    </th>
                    <th
                      className={styles["admin-billing-manage-view__th"]}
                      style={{ textAlign: "right" }}
                    >
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={styles["admin-billing-manage-view__td"]}>
                      {data.plan_name} - {data.add_credits} Credits
                    </td>
                    <td
                      className={styles["admin-billing-manage-view__td"]}
                      style={{ textAlign: "center" }}
                    >
                      1
                    </td>
                    <td
                      className={styles["admin-billing-manage-view__td"]}
                      style={{ textAlign: "right" }}
                    >
                      Rs {data.amount}
                    </td>
                    <td
                      className={styles["admin-billing-manage-view__td"]}
                      style={{ textAlign: "right" }}
                    >
                      Rs {data.amount}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Totals Calculation */}
            <div
              className={styles["admin-billing-manage-view__totals-wrapper"]}
            >
              <div className={styles["admin-billing-manage-view__totals"]}>
                <div className={styles["admin-billing-manage-view__total-row"]}>
                  <span>Subtotal</span>
                  <span>Rs {data.amount}</span>
                </div>

                {isRajasthan ? (
                  <>
                    <div
                      className={styles["admin-billing-manage-view__total-row"]}
                    >
                      <span>CGST ({data.gst / 2}%)</span>
                      <span>Rs {data.gst_amount / 2}</span>
                    </div>
                    <div
                      className={styles["admin-billing-manage-view__total-row"]}
                    >
                      <span>SGST ({data.gst / 2}%)</span>
                      <span>Rs {data.gst_amount / 2}</span>
                    </div>
                  </>
                ) : (
                  <div
                    className={styles["admin-billing-manage-view__total-row"]}
                  >
                    <span>IGST ({data.gst}%)</span>
                    <span>Rs {data.gst_amount}</span>
                  </div>
                )}

                <div
                  className={`${styles["admin-billing-manage-view__total-row"]} ${styles["admin-billing-manage-view__total-row--grand"]}`}
                >
                  <span>Total</span>
                  <span>Rs {data.total_amount}</span>
                </div>

                <div
                  className={`${styles["admin-billing-manage-view__total-row"]} ${styles["admin-billing-manage-view__total-row--paid"]}`}
                >
                  <span>Amount Paid</span>
                  <span>Rs {data.total_amount}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={styles["admin-billing-manage-view__footer"]}>
              <span
                className={styles["admin-billing-manage-view__footer-thanks"]}
              >
                Thank you for your business!
              </span>
              <span
                className={styles["admin-billing-manage-view__footer-contact"]}
              >
                For questions about this invoice, please contact{" "}
                <a
                  href="mailto:support@doql.com"
                  className={styles["admin-billing-manage-view__footer-link"]}
                >
                  support@doql.com
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingManagementView;
